import { type CSSProperties } from "react";

import clsx from "clsx";

import salesPlatformColumnLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { type ColumnDef } from "@features/landing-page-analytics/types";

import { CellLoadingIndictor } from "../body-components/cell-loading-indicator";
import {
  NAME_COLUMN_ID,
  SELECTION_COLUMN_WIDTH,
  URL_COLUMN_ID,
} from "../constants";
import { MetricDataFormatter } from "../data-type-formatters";
import { type LandingPageAnalyticsTableRow } from "../types";

interface Cell {
  colSpan: number;
  column: {
    type?: unknown;
    column: ColumnDef;
  };
}

interface Props {
  className?: string;
  style?: CSSProperties;
  isFixed: boolean;
  children: JSX.Element[];
}

export const TableHeadWithFixedRow =
  (summaryRow: LandingPageAnalyticsTableRow, loading: boolean) =>
  ({ isFixed, className, style, children }: Props) => {
    const columnsInView: Cell[] = children[0].props.cells;

    return (
      <thead
        className={clsx(
          { "dx-g-bs4-fixed-header dx-g-bs4-table-sticky bg-white": isFixed },
          className,
        )}
        style={style}
      >
        {children}
        <tr>
          {columnsInView.map(({ column }: Cell, index: number) => (
            <ColumnHead
              key={index}
              column={column}
              tableSummary={summaryRow}
              loading={loading}
            />
          ))}
        </tr>
      </thead>
    );
  };

const ColumnHead = ({
  column,
  tableSummary,
  loading,
}: {
  column: Cell["column"];
  tableSummary: LandingPageAnalyticsTableRow;
  loading: boolean;
}) => {
  // Not sure why but there's s stub header column
  const columnType = column?.type?.toString();

  if (columnType && ["Symbol(stub)", "Symbol(flex)"].includes(columnType)) {
    return <th className="p-0 table-summary-cell" scope="col"></th>;
  }

  if (columnType === "Symbol(select)") {
    return (
      <th
        className="table-summary-cell left-0 p-0 dx-g-bs4-fixed-cell position-sticky z-[1]"
        scope="col"
      ></th>
    );
  }

  // Only a known data column would have a column property
  if (!column?.column) {
    return null;
  }

  if (column.column.name === URL_COLUMN_ID) {
    return (
      <th
        className="table-summary-cell position-sticky z-[1]"
        style={{ left: `${SELECTION_COLUMN_WIDTH}px` }}
        scope="col"
      >
        {loading ? (
          <div className="flex items-center h-full ml-2">
            <img
              src={salesPlatformColumnLoading}
              alt="Loading table"
              style={{
                height: "2rem",
              }}
            />
          </div>
        ) : (
          <div className="flex items-center h-full px-3">
            <div className="flex flex-col justify-center leading-3">
              <div>Total / AVG</div>
            </div>
          </div>
        )}
      </th>
    );
  }

  if (column.column.name === NAME_COLUMN_ID) {
    return (
      <th className="table-summary-cell z-[1]" scope="col">
        {loading && <CellLoadingIndictor />}
      </th>
    );
  }

  return (
    <th key={column.column.name} className="table-summary-cell z-1" scope="col">
      {loading ? (
        <CellLoadingIndictor />
      ) : (
        <MetricDataFormatter
          row={tableSummary}
          column={column.column}
          value={null}
        />
      )}
    </th>
  );
};
